import React, { useEffect, useState, useRef, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { supabase } from './services/supabaseClient';
import 'amazon-connect-streams';
import { Zap, Activity, Lightbulb, MessageSquare, AlertCircle, Moon, Sun, Sparkles, LayoutDashboard } from 'lucide-react';
import Login from './components/Login';
import SessionStatus from './components/SessionStatus';
import SalesForm from './components/SalesForm';
import ConsoleNavigation from './components/ConsoleNavigation';
import SalesEntry from './components/SalesEntry';
import SalesScriptPanel from './components/SalesScriptPanel';
import LeadInfoCard from './components/LeadInfoCard';
import Customer360Panel from './components/Customer360Panel';
import CallTracePanel from './components/CallTracePanel';
import { useAuth } from './context/AuthContext';
import { SalesDataProvider, useSalesData } from './context/SalesDataContext';
import { useCapabilities } from './hooks/useCapabilities';
import { useConsoleNavigation } from './hooks/useConsoleNavigation';
import { useTramite } from './context/TramiteContext';
import CapabilityGate from './components/common/CapabilityGate';
import OfferSection from './components/OfferSection';
import BackofficeLayout from './components/backoffice/BackofficeLayout';
import AgencySupervisor from './components/backoffice/AgencySupervisor';
import { AppendixProvider } from './context/AppendixContext';
import AppendixTabsBar from './components/AppendixTabsBar';
import RoleSelector from './components/RoleSelector';
import WhatsAppInboxSidebar from './components/WhatsAppInboxSidebar';
import WhatsAppConversationAppendix from './components/WhatsAppConversationAppendix';
import ZohoDeskTicketsAppendix from './components/ZohoDeskTicketsAppendix';
import LeadInfoSidebar from './components/LeadInfoSidebar';
import SecondSidebar from './components/common/SecondSidebar';
import { useTheme } from './context/ThemeContext';
import { useAppendix } from './context/AppendixContext';
import CallTraceAppendix from './components/CallTraceAppendix';
import CatcherAppendix from './components/CatcherAppendix';
import LeadDeepSearchAppendix from './components/LeadDeepSearchAppendix';

// CallTraceWrapper removed to consolidate on draggable appendices

// Removed local supabase initialization as it's now imported from services/supabaseClient


// Mute Amazon Connect logs except errors
if (window.connect && window.connect.Log) {
    window.connect.Log.getLog().setLogLevel(window.connect.LogLevel.ERROR);
}

// Helper for status styles
const getStatusStyle = (status) => {
    switch (status) {
        case 'EN LLAMADA': return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30 shadow-[0_0_15px_-3px_rgba(16,185,129,0.1)]';
        case 'FINALIZADA': return 'bg-red-500/10 text-red-500 border-red-500/30 shadow-[0_0_15px_-3px_rgba(239,68,68,0.1)]';
        default: return 'bg-slate-500/10 text-slate-400 border-slate-800 shadow-none';
    }
};

// Extracted AgentConsole to a stable standalone component
export const AgentConsole = ({
    token,
    hasCapability,
    agentInfo,
    logout,
    callStatus,
    setCallStatus,
    currentTramite,
    setCurrentTramite,
    startTramite,
    authFetch,
    tramiteId,
    contactId,
    tips,
    closeTramite
}) => {
    const [streamContactId, setStreamContactId] = useState(null);
    const [openedWaConversationId, setOpenedWaConversationId] = useState(null);
    const [historicalWaConversationId, setHistoricalWaConversationId] = useState(null);
    const [callTracePhones, setCallTracePhones] = useState([]);
    const { openAppendix } = useAppendix();
    const [pendingWaMessageId, setPendingWaMessageId] = useState(null);
    const [stolenMessage, setStolenMessage] = useState(null);
    const { activeModule, selectModule, salesEnabled, invoicesEnabled } = useConsoleNavigation(currentTramite?.id);
    const whatsappEnabled = hasCapability('gestion_whatsapp');
    useEffect(() => {
        // The inbox owns the title when mounted, including its unread count.
        if (whatsappEnabled) return undefined;
        document.title = 'Consola Agente';
        return () => { document.title = 'Consola Agente'; };
    }, [whatsappEnabled]);
    const [isWaInboxExpanded, setIsWaInboxExpanded] = useState(() => {
        try {
            return localStorage.getItem('cti_whatsapp_inbox_expanded') === 'true';
        } catch (e) {
            return false;
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem('cti_whatsapp_inbox_expanded', isWaInboxExpanded);
        } catch (e) {
            // ignore
        }
    }, [isWaInboxExpanded]);
    const [notificationPermission, setNotificationPermission] = useState(
        'Notification' in window ? Notification.permission : 'default'
    );
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const ccpInitialized = useRef(false);
    // Use refs for listeners to avoid stale closures
    const currentTramiteRef = useRef(currentTramite);
    const agentInfoRef = useRef(agentInfo);
    const setCurrentTramiteRef = useRef(setCurrentTramite);
    const startTramiteRef = useRef(startTramite);
    const closeTramiteRef = useRef(closeTramite);

    useEffect(() => { currentTramiteRef.current = currentTramite; }, [currentTramite]);
    useEffect(() => { agentInfoRef.current = agentInfo; }, [agentInfo]);
    useEffect(() => { setCurrentTramiteRef.current = setCurrentTramite; }, [setCurrentTramite]);
    useEffect(() => { startTramiteRef.current = startTramite; }, [startTramite]);
    useEffect(() => { closeTramiteRef.current = closeTramite; }, [closeTramite]);

    useEffect(() => {
        if (!hasCapability('gestion_whatsapp')) return;
        
        if ('Notification' in window) {
            if (Notification.permission === 'default') {
                Notification.requestPermission().then(permission => {
                    setNotificationPermission(permission);
                });
            } else {
                setNotificationPermission(Notification.permission);
            }
        }
    }, [hasCapability]);

    useEffect(() => {
        if (!token || !hasCapability('telefoniam')) return;
        if (ccpInitialized.current) return;

        /* eslint-disable no-undef */
        const container = document.getElementById('ccp-container');
        if (container) {
            try {
                window.connect.core.initCCP(container, {
                    ccpUrl: process.env.REACT_APP_CONNECT_URL || 'https://espuma.my.connect.aws/connect/ccp-v2/',
                    loginPopup: true,
                    region: 'eu-west-1',
                    softphone: { allowFramedSoftphone: true }
                });
                window.connect.contact(async contact => {
                    const newContactId = contact.getContactId();
                    const initialId = contact.getInitialContactId();
                    const effectiveId = initialId || newContactId;

                    // Set temporary ID for LeadInfo visibility during ringing
                    setStreamContactId(effectiveId);

                    contact.onConnected(async () => {
                        setCallStatus('EN LLAMADA');

                        try {
                            const agentEmail = agentInfoRef.current?.email || 'agente@espuma.com';
                            let callerPhone = null;
                            let calleePhone = null;

                            const normalizePhone = (p) => {
                                if (!p) return null;
                                let clean = String(p).replace(/\s+/g, '').replace(/-/g, '');
                                if (clean.startsWith('+34')) clean = clean.substring(3);
                                if (clean.startsWith('0034')) clean = clean.substring(4);
                                return clean;
                            };

                            try {
                                const initialConnection = contact.getInitialConnection();
                                if (initialConnection) {
                                    const endpoint = initialConnection.getEndpoint();
                                    if (endpoint) callerPhone = normalizePhone(endpoint.phoneNumber);
                                }
                                const attributes = contact.getAttributes();
                                if (attributes && attributes.customerNumber) {
                                    callerPhone = normalizePhone(attributes.customerNumber.value) || callerPhone;
                                }
                            } catch (phoneErr) {
                                console.warn("Error extrayendo teléfonos de Connect contact:", phoneErr);
                            }

                            if (effectiveId.startsWith('test-')) {
                                callerPhone = callerPhone || "600111222";
                                calleePhone = calleePhone || "911222333";
                            }

                            // /calls/start ahora resuelve el trámite automáticamente por teléfono
                            const response = await authFetch('/api/calls/start', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    contactId: effectiveId,
                                    agentEmail,
                                    callerPhone,
                                    calleePhone
                                    // NO enviar tramiteId - el backend lo resuelve automáticamente
                                })
                            });

                            if (response.ok) {
                                const callData = await response.json();
                                const { tramite_id, sales_form_data } = callData;
                                
                                // Actualizar contexto de trámite si el backend lo creó/encontró
                                if (tramite_id && setCurrentTramiteRef.current) {
                                    // Establecer el trámite resuelto automáticamente por el backend
                                    setCurrentTramiteRef.current({
                                        id: tramite_id,
                                        contact_id: effectiveId,
                                        status: 'OPEN'
                                    });
                                    console.log('[App] ✅ Trámite auto-resuelto y cargado:', tramite_id);
                                    
                                    // Si hay datos previos, se cargarán automáticamente en SalesForm
                                    if (sales_form_data && Object.keys(sales_form_data).length > 0) {
                                        console.log('[App] 💾 Datos del formulario pre-cargados desde trámite existente');
                                    }
                                }
                                
                                // Iniciar sales_form
                                authFetch('/api/sale/start', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                        contact_id: effectiveId,
                                        tramite_id: tramite_id
                                    })
                                }).catch(err => console.error("Error starting sales form:", err));
                            }
                        } catch (e) {
                            console.error("Error en flujo onConnected:", e);
                        }
                    });

                    contact.onEnded(() => {
                        setCallStatus('FINALIZADA');
                        authFetch('/api/calls/end', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ contactId: effectiveId })
                        }).catch(err => console.error("Error al finalizar llamada:", err));
                    });

                    contact.onDestroy(() => {
                        setCallStatus('ESPERANDO');
                        setStreamContactId(null);
                        // NO cerrar el trámite automáticamente - el agente puede seguir trabajando
                        // El trámite se cierra solo al enviar el formulario de venta
                        console.log('[App] Llamada finalizada - Trámite sigue activo');
                    });
                });
                ccpInitialized.current = true;
            } catch (e) {
                console.error("CCP Init Error", e);
            }
        }
    }, [authFetch, hasCapability, token, setCallStatus]);

    return (
        <SalesDataProvider 
            tramiteId={tramiteId} 
            contactId={streamContactId || contactId} 
            authFetch={authFetch}
            preHydratedData={currentTramite?.preHydratedData}
            conversationMetadata={currentTramite?.conversationMetadata}
        >
            <div className="flex h-screen bg-[#020817] text-slate-50 overflow-hidden font-sans">
                <aside className={`${isWaInboxExpanded ? 'w-[600px]' : 'w-[291px]'} bg-[#0f172a] border-r border-slate-800 flex flex-col shadow-2xl z-20 overflow-hidden shrink-0 transition-all duration-200`}>
                    <div className="p-6 border-b border-slate-800 flex flex-col items-center justify-center">
                        <img
                            src="https://imaginaenergia.com/wp-content/themes/uncode/assets/images/imagina_energia_logo.svg"
                            alt="Imagina Energía"
                            className="h-10 w-auto"
                        />
                    </div>

                    <CapabilityGate capability="telefoniam">
                        <div className="flex-1 flex flex-col border-t border-slate-800">
                            <div id="ccp-container" className="h-[481px] flex-shrink-0" />
                        </div>
                    </CapabilityGate>



                    <CapabilityGate capability="gestion_whatsapp">
                        <WhatsAppInboxSidebar 
                            activeConversationId={openedWaConversationId}
                            isExpanded={isWaInboxExpanded}
                            onToggleExpand={() => setIsWaInboxExpanded(prev => !prev)}
                            onOpenHistoricalConversation={(id) => setHistoricalWaConversationId(id)}
                            onOpenCallTrace={(phones) => {
                                setCallTracePhones(phones);
                                openAppendix('call-trace');
                            }}
                            onOpenConversation={(payload) => {
                                // Support both old string payload and new object payload
                                if (!payload) return;
                                if (salesEnabled) selectModule('sales');
                                if (typeof payload === 'string') {
                                    setOpenedWaConversationId(payload);
                                } else if (typeof payload === 'object') {
                                    setOpenedWaConversationId(payload.conversation_id);
                                    if (payload.wa_message_id) {
                                        setPendingWaMessageId(payload.wa_message_id);
                                    }
                                }
                            }}
                            onStolen={(thiefEmail) => {
                                // 1. Cerrar chat
                                setOpenedWaConversationId(null);
                                // 2. Activar aviso (Mantenemos currentTramite para NO borrar campos)
                                const thiefName = thiefEmail ? thiefEmail.split('@')[0] : 'otro agente';
                                setStolenMessage(`La conversación ha sido tomada por el agente ${thiefName}. Ya no puedes gestionarla.`);
                            }}
                        />
                    </CapabilityGate>
                </aside>
                

                <main className="flex-1 flex flex-col overflow-hidden bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-black relative">
                    <div className="contents">
                    {hasCapability('gestion_whatsapp') && notificationPermission === 'denied' && (
                        <div className="bg-amber-500/10 border-b border-amber-500/20 text-amber-500 text-[11px] font-bold uppercase tracking-wider px-6 py-2 flex items-center justify-center gap-2 z-50">
                            <AlertCircle size={14} />
                            Las notificaciones de escritorio están bloqueadas en tu navegador. Actívalas para recibir alertas de WhatsApp.
                        </div>
                    )}
                    <header className="h-20 border-b border-slate-800 flex items-center justify-between px-10 bg-slate-950/50 backdrop-blur-md sticky top-0 z-10 transition-all">
                        <div className="flex items-center gap-4">
                            <div className="p-2 bg-primary/10 rounded-lg border border-primary/20">
                                <Zap className="text-primary w-6 h-6" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold tracking-tight">Consola de Agente <span className="text-primary text-sm font-normal ml-2 opacity-80 underline decoration-primary/30">v{agentInfo?.version || '0.15.21'}</span></h1>
                                {agentInfo && (
                                    <p className="text-slate-400 text-sm mt-1">Sesión: <span className="text-slate-200">{agentInfo.full_name || agentInfo.email}</span></p>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-6">
                            <AppendixTabsBar />

                            <RoleSelector />

                            {hasCapability('catcher') && (
                                <button
                                    onClick={() => openAppendix('catcher')}
                                    title="Abrir Catcher"
                                    className="p-2 text-fuchsia-400 hover:text-fuchsia-300 hover:bg-fuchsia-500/10 rounded-full transition-colors"
                                >
                                    <Sparkles size={20} />
                                </button>
                            )}

                            {hasCapability('lead_deep_search') && (
                                <button
                                    onClick={() => openAppendix('lead-deep-search')}
                                    title="Búsqueda de Leads"
                                    className="p-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 rounded-full transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="11" cy="11" r="8"/>
                                        <path d="m21 21-4.3-4.3"/>
                                    </svg>
                                </button>
                            )}

                            {(hasCapability('backoffice') || hasCapability('whatsapp_admin')) && (
                                <button
                                    onClick={() => {
                                        if (hasCapability('backoffice')) {
                                            navigate('/backoffice');
                                        } else if (hasCapability('whatsapp_admin')) {
                                            navigate('/backoffice/whatsapp-conversations');
                                        }
                                    }}
                                    title="Ir al Backoffice"
                                    className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-full transition-colors"
                                >
                                    <LayoutDashboard size={20} />
                                </button>
                            )}

                            <button
                                onClick={toggleTheme}
                                className="p-2 text-slate-500 hover:text-slate-300 hover:bg-slate-800/50 rounded-full transition-colors mr-4"
                                title="Cambiar Tema"
                            >
                                {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
                            </button>
                            <button
                                onClick={logout}
                                className="text-slate-500 hover:text-slate-300 text-xs font-mono uppercase tracking-widest transition-colors mr-6"
                            >
                                Cerrar Sesión
                            </button>
                            <div className={`flex items-center gap-3 px-4 py-2 rounded-full border shadow-sm transition-all duration-300 ${getStatusStyle(callStatus)}`}>
                                <Activity size={16} className={callStatus === 'EN LLAMADA' ? 'animate-pulse' : ''} />
                                <span className="text-sm font-bold tracking-wide uppercase">{callStatus}</span>
                            </div>
                        </div>
                    </header>

                    <ConsoleNavigation activeModule={activeModule} salesEnabled={salesEnabled}
                        invoicesEnabled={invoicesEnabled} onSelect={selectModule} />
                    </div>

                    <div className="flex-1 flex overflow-hidden">
                        <div className="contents"><SecondSidebar>
                            <LeadInfoSidebar />
                        </SecondSidebar></div>

                        <div className="flex-1 flex flex-col overflow-hidden relative">

                    {activeModule === 'home' && <div id="console-home" className="flex-1" />}

                    <div id="console-sales" hidden={activeModule !== 'sales'} className={activeModule === 'sales' ? 'contents' : 'hidden'}>
                    <CapabilityGate
                        capability="ventas_electricidad"
                    >
                        <div className={`flex-1 p-6 overflow-y-auto custom-scrollbar ${hasCapability('coach') ? 'pr-[380px]' : 'pr-6'}`}>
                            <div className="max-w-7xl mx-auto lg:mx-0 xl:mx-auto space-y-8">
                                <CapabilityGate capability="calltrace">
                                    {/* CallTraceWrapper removed to consolidate on draggable appendices */}
                                </CapabilityGate>

                                <CapabilityGate capability="offers">
                                    <OfferSection />
                                </CapabilityGate>

                                <div className="animate-in fade-in duration-700 relative">
                                    {(stolenMessage || !currentTramite) && (
                                        stolenMessage ? (
                                            <div className="absolute inset-0 z-50 bg-slate-950/80 backdrop-blur-[4px] rounded-2xl flex items-center justify-center border border-slate-800">
                                                <div className="text-center p-8 bg-slate-900 shadow-2xl rounded-3xl border border-rose-900/50 flex flex-col items-center">
                                                    <div className="p-4 bg-rose-500/10 rounded-full mb-4">
                                                        <AlertCircle size={48} className="text-rose-500 animate-pulse" />
                                                    </div>
                                                    <h2 className="text-2xl font-bold mb-3 text-slate-100">Conversación Reasignada</h2>
                                                    <p className="text-slate-300 max-w-sm mb-6">{stolenMessage}</p>
                                                    <button 
                                                        onClick={() => {
                                                            setStolenMessage(null);
                                                            setCurrentTramite(null);
                                                        }}
                                                        className="px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl transition-colors font-medium border border-slate-700 hover:border-slate-600"
                                                    >
                                                        Entendido
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <SalesEntry onStart={() => startTramite()}
                                                hasWhatsApp={hasCapability('gestion_whatsapp')} />
                                        )
                                    )}

                                    {!stolenMessage && currentTramite && (
                                        <SalesForm 
                                            contactId={contactId} 
                                            openedWaConversationId={openedWaConversationId}
                                            setOpenedWaConversationId={setOpenedWaConversationId}
                                            onOpenHistoricalConversation={(id) => setHistoricalWaConversationId(id)}
                                            onOpenCallTrace={(phones) => {
                                                setCallTracePhones(phones);
                                                openAppendix('call-trace');
                                            }}
                                            pendingWaMessageId={pendingWaMessageId}
                                            setPendingWaMessageId={setPendingWaMessageId}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>

                        <CapabilityGate capability="customer360">
                            {(streamContactId || contactId) && !String(streamContactId || contactId).includes('manual') && (
                                <Customer360Panel contactId={streamContactId || contactId} />
                            )}
                        </CapabilityGate>

                            <CapabilityGate capability="coach">
                                <div className="pointer-events-auto">
                                    <SalesScriptPanel contactId={contactId} />
                                    <div className="fixed top-24 right-10 w-[340px] bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-2xl shadow-2xl overflow-hidden z-30 flex flex-col">
                                        <div className="p-5 border-b border-slate-800 bg-slate-900/50 flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-primary/10 rounded-lg">
                                                    <Lightbulb size={18} className="text-primary" />
                                                </div>
                                                <h3 className="font-bold text-slate-100 tracking-tight">IA Coach: Sugerencias</h3>
                                            </div>
                                        </div>
                                        <div className="p-4 max-h-[calc(100vh-200px)] overflow-y-auto custom-scrollbar space-y-4">
                                            {tips.length === 0 ? (
                                                <div className="flex flex-col items-center justify-center py-10 px-6 border-2 border-dashed border-slate-800 rounded-2xl bg-slate-900/20 text-slate-500 text-center">
                                                    <Lightbulb size={24} className="opacity-20 mb-2" />
                                                    <p className="text-xs">Esperando flujo de audio...</p>
                                                </div>
                                            ) : (
                                                tips.map((tip) => (
                                                    <div key={tip.id} className="bg-slate-800/40 rounded-xl p-4 border border-slate-700/50">
                                                        <p className="text-slate-200 text-xs">{tip.tip_text}</p>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </CapabilityGate>
                    </CapabilityGate>
                    </div>

                    <div className="contents">
                    {/* Keep the active and historical conversation state mounted. */}
                    {/* WhatsApp Chat Conversation (Primary/Active) */}
                    <WhatsAppConversationAppendix 
                        isOpen={!!openedWaConversationId}
                        onClose={() => setOpenedWaConversationId(null)}
                        conversationId={openedWaConversationId}
                        onOpenHistoricalConversation={(id) => setHistoricalWaConversationId(id)}
                        onOpenCallTrace={(phones) => {
                            setCallTracePhones(phones);
                            openAppendix('call-trace');
                        }}
                        onStolen={(thiefEmail) => {
                            setOpenedWaConversationId(null);
                            setCurrentTramite(null); // Clear context immediately
                            setStolenMessage(`La conversación ha sido tomada por el usuario ${thiefEmail.split('@')[0]}. Ya no puedes gestionarla.`);
                        }}
                    />

                    {/* WhatsApp Chat Conversation (Historical/ReadOnly) */}
                    <WhatsAppConversationAppendix 
                        isOpen={!!historicalWaConversationId}
                        onClose={() => setHistoricalWaConversationId(null)}
                        conversationId={historicalWaConversationId}
                        isHistorical={true}
                        onOpenCallTrace={(phones) => {
                            setCallTracePhones(phones);
                            openAppendix('call-trace');
                        }}
                    />

                    {/* Global CallTrace Appendix */}
                    <CallTraceAppendix 
                        phones={callTracePhones}
                        onOpenWhatsAppConversation={(id) => setHistoricalWaConversationId(id)}
                    />

                    {/* Catcher Appendix — análisis de conversaciones de WhatsApp */}
                    <CapabilityGate capability="catcher">
                        <CatcherAppendix
                            tramiteId={currentTramite?.id || null}
                            conversationId={openedWaConversationId}
                        />
                    </CapabilityGate>

                    {/* Lead Deep Search Appendix — búsqueda profunda de leads */}
                    <CapabilityGate capability="lead_deep_search">
                        <LeadDeepSearchAppendix />
                    </CapabilityGate>
                    </div>
                        </div>
                    </div>
                </main>
            </div>
        </SalesDataProvider>
    );
};

const App = () => {
    const { token, agentInfo, login, logout, authFetch, loading, sessionError, retrySession } = useAuth();
    const { currentTramite, setCurrentTramite, startTramite, closeTramite, isResuming } = useTramite();
    const [tips, setTips] = useState([]);
    const [callStatus, setCallStatus] = useState('ESPERANDO');
    const { hasCapability } = useCapabilities();

    const tramiteId = currentTramite?.id || null;
    const contactId = currentTramite?.contact_id || null;

    // Debug logging for tramite changes
    useEffect(() => {
        console.log('[App] currentTramite changed:', currentTramite);
        console.log('[App] Derived tramiteId:', tramiteId);
        console.log('[App] Derived contactId:', contactId);
    }, [currentTramite, tramiteId, contactId]);

    useEffect(() => {
        // Automatically start tramite logic removed as per user request
    }, [isResuming, currentTramite, hasCapability, startTramite]);

    useEffect(() => {
        if (!contactId || !hasCapability('coach')) {
            setTips([]);
            return;
        }

        const fetchExistingTips = async () => {
            try {
                const res = await authFetch(`/api/calls/tips/${contactId}`);
                if (res.ok) {
                    const data = await res.json();
                    setTips(data.tips || []);
                }
            } catch (err) {
                console.error("Error fetching existing tips:", err);
            }
        };
        fetchExistingTips();

        const channel = supabase
            .channel(`espuma-ai-tips-${contactId}`)
            .on(
                'postgres_changes',
                { event: 'INSERT', schema: 'espuma', table: 'ai_tips' },
                payload => {
                    if (payload.new && String(payload.new.contact_id) === String(contactId)) {
                        setTips(prev => [payload.new, ...prev]);
                    }
                }
            )
            .subscribe();

        return () => { supabase.removeChannel(channel); };
    }, [contactId, authFetch, hasCapability]);

    if (loading || sessionError) return <SessionStatus loading={loading} error={sessionError} onRetry={retrySession} onLogout={logout} />;
    if (!token) return <Login onLoginSuccess={login} />;
    if (agentInfo?.permissions?.agency_scope || hasCapability('agency_supervisor')) return <AgencySupervisor />;

    return (
        <AppendixProvider>
            <BrowserRouter>
                <Routes>
                    <Route path="/login" element={<Navigate to="/" replace />} />
                    <Route path="/" element={
                        <AgentConsole
                            token={token}
                            hasCapability={hasCapability}
                            agentInfo={agentInfo}
                            logout={logout}
                            callStatus={callStatus}
                            setCallStatus={setCallStatus}
                            currentTramite={currentTramite}
                            startTramite={startTramite}
                            authFetch={authFetch}
                            tramiteId={tramiteId}
                            contactId={contactId}
                            tips={tips}
                            setCurrentTramite={setCurrentTramite}
                            closeTramite={closeTramite}
                        />
                    } />
                    <Route
                        path="/backoffice/*"
                        element={(hasCapability('backoffice') || hasCapability('whatsapp_admin')) ? <BackofficeLayout /> : <Navigate to="/" replace />}
                    />
                </Routes>
            </BrowserRouter>
        </AppendixProvider>
    );
};

export default App;
